import React, {FormEvent, MouseEvent, useCallback, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import intl from 'react-intl-universal';
import {useAppState, useRootStore} from "@stores/AppStateContext";

import {AWSAPIError} from "../../../../../utils/types";

import {isValidEmail} from "../../../../../utils/isValidEmail";
import {MaterialUiInput} from "../../../../components/form/material-ui-input/MaterialUiInput";
import {TextLink} from "../../../../components/TextLink";
import {Button} from "../../../../components/Button";
import {SocialSignInRow} from '../components/SocialSignInRow';
import {AuthModal, AuthModalHeader, logIn} from "../AuthModal";

import {MODALS} from '../../modal-names';
import {pageAdditionalParamsRef} from "../../../../../volatile/pageAdditionalParams";

interface FormData {
    email: { value: string };
    password: { value: string };
}

export interface LoginAdditionalProps {
    emailDefaultValue?: string;
    passwordDefaultValue?: string;
}

const LoginBase = () => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const {emailDefaultValue, passwordDefaultValue} = pageAdditionalParamsRef.current as LoginAdditionalProps;
    const handleClose = useCallback(() => {
    }, []);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [loginError, setError] = useState<string | undefined>(undefined);
    const [isProcessing, setIsProcessing] = useState(false);
    const isSubmittingRef = useRef(false);

    const [email, setEmail] = useState(emailDefaultValue ? emailDefaultValue : '');
    const [password, setPassword] = useState(passwordDefaultValue ? passwordDefaultValue : '');

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;

        setError(undefined);
        if (!isProcessing) {
            setIsProcessing(true);

            if (!isValidEmail(email)) {
                setError(intl.get('invalid.email.format').d("Please enter your email address using the format name@example.com"));
                isSubmittingRef.current = false;
                setIsProcessing(false);
                return;
            }

            try {
                await logIn(appState, rootStore.view, email, password);
                isSubmittingRef.current = false;
                setIsProcessing(false);
            } catch (error: unknown) {
                isSubmittingRef.current = false;
                setIsProcessing(false);

                switch ((error as AWSAPIError).name) {
                    case 'UserNotFoundException': {
                        setError(intl.get('aws_api_error.UserNotFoundException').d("The email or password you entered doesn't match our records. Please double-check and try again."));
                        break;
                    }
                    case 'LimitExceededException': {
                        appState.setModal({
                            name: MODALS.LOCKED, params: {
                                reason: 'login_limit'
                            }
                        });
                        break;
                    }
                    default:
                        setError((error as Error).message);
                }
            }
        }
    };

    const handleCreateAccountClick = useCallback((event: MouseEvent) => {
        event.preventDefault();
        rootStore.view.openPageByName('sign-up', {}, {}, {
            emailDefaultValue: email.trim(),
            passwordDefaultValue: password.trim(),
        });
    }, [email, password]);
    const activeModal = appState.topModal;

    return (
        <AuthModal
            isOpen={activeModal === MODALS.LOGIN}
            onClose={handleClose}
        >
            <form onSubmit={handleSubmit}>
                <AuthModalHeader title={intl.get('login.header').d('Log in')}/>
                <MaterialUiInput
                    className={'AuthModalBodyStart Auth-input'}
                    name="email"
                    isRequired={true}
                    fullWidth={true}
                    label={intl.get('sign_up.email').d('Email')}
                    error={loginError}
                    value={email}
                    onChange={(value) => setEmail(value.trim())}
                    emptyFieldError={intl.get('email.empty.field').d("Please provide your email")}
                />
                <MaterialUiInput
                    className={'Auth-input Login-pwd'}
                    name="password"
                    type={'password'}
                    showPassword={showNewPassword}
                    setShowPassword={setShowNewPassword}
                    isRequired={true}
                    fullWidth={true}
                    value={password}
                    onChange={(value) => setPassword(value.trim())}
                    label={intl.get('sign_up.password').d('Password')}
                    emptyFieldError={intl.get('password.empty.field').d("Please provide your password")}
                />
                <div className="Auth-text-line">
                    <Button
                        onClick={() => {
                            rootStore.view.openPageByName('reset-password')
                        }}
                        text={intl.get('login.forgot_password').d(`Forgot password`)}
                        style={'plain'}
                        type={'primary'}
                        size={'sm'}
                    />
                </div>
                <Button
                    type="primary"
                    className="Auth-btn"
                    htmlType="submit"
                    fullWidth
                    size={'lg'}
                    text={intl.get('login.log_in').d('Log in')}
                    loading={isProcessing}
                />
                <SocialSignInRow lineText={intl.get('social-sign-in.line.login').d('or sign in with')}/>
                <div className="Auth-policy-info body-medium">
                    <span>{intl.get('login.dont_have').d(`Don't have account?`)}</span>
                    <TextLink
                        className={'Auth__inline-btn body-medium'}
                        onClick={handleCreateAccountClick}
                        text={intl.get('login.create_account').d('Create Account')}
                    />
                </div>
            </form>
        </AuthModal>
    );
}

export const Login = observer(LoginBase);

